import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Button, Typography, Snackbar, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, TableSortLabel, Pagination, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, Card, CardContent, Grid, CircularProgress, Skeleton, Link, Dialog, DialogContent, DialogTitle, IconButton, TextField, InputAdornment, Menu, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ResponseTemplate from '../../components/Surveys/ResponseTemplate';
import './SurveyDashboard.css';

const SurveyDashboard = () => {
  const navigate = useNavigate();

  // Project-survey flow state
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectContacts, setProjectContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');

  // Multi-select contacts (store contact IDs as strings)
  const [selectedContacts, setSelectedContacts] = useState([]); // array of contact ID strings
  const [selectAllContacts, setSelectAllContacts] = useState(false);

  // For generating survey links
  const [surveyUniqueIds, setSurveyUniqueIds] = useState({});

  // Client-survey flow state
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientOwners, setClientOwners] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]); // array of owner ID strings
  const [selectAllOwners, setSelectAllOwners] = useState(false);
  const [clientSurveyId, setClientSurveyId] = useState('');
  const [clientSurveyUniqueIds, setClientSurveyUniqueIds] = useState({});

  // Dropdown open/close
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
  const projectDropdownRef = useRef(null);
  const contactDropdownRef = useRef(null);
  const ownerDropdownRef = useRef(null);
  const clientDropdownRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' | 'error'
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [surveysSentData, setSurveysSentData] = useState([]);
  const [loadingSurveysSent, setLoadingSurveysSent] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sorting state
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');

  // Survey type toggle
  const [surveyType, setSurveyType] = useState('project');
  const [clientSurveysData, setClientSurveysData] = useState([]);
  const [loadingClientSurveys, setLoadingClientSurveys] = useState(false);

  // Project type filter and send survey toggle
  const [projectType, setProjectType] = useState('');
  const [showSendSurvey, setShowSendSurvey] = useState(() => {
    const saved = sessionStorage.getItem('showSendSurvey');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Response received data
  const [projectResponsesData, setProjectResponsesData] = useState([]);
  const [clientResponsesData, setClientResponsesData] = useState([]);
  const [loadingProjectResponses, setLoadingProjectResponses] = useState(false);
  const [loadingClientResponses, setLoadingClientResponses] = useState(false);

  // Dialog state for response template
  const [openResponseDialog, setOpenResponseDialog] = useState(false);
  const [selectedSurveyResponse, setSelectedSurveyResponse] = useState({ surveyId: null, clientId: null, responseDetails: null });

  // Search states for all tabs
  const [projectSurveySearch, setProjectSurveySearch] = useState('');
  const [clientSurveySearch, setClientSurveySearch] = useState('');
  const [projectResponseSearch, setProjectResponseSearch] = useState('');
  const [clientResponseSearch, setClientResponseSearch] = useState('');

  // Export menu anchor
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [currentExportData, setCurrentExportData] = useState({ data: [], columns: [], filename: '' });

  // Column visibility states
  const [projectSurveyColumns, setProjectSurveyColumns] = useState({ ProjectName: true, ClientName: true, Practice: false, SurveyName: true, RecipientName: false, RecipientEmail: false, SentOn: true, ResponseStatus: true, RespStartTime: false, Aging: false });
  const [clientSurveyColumns, setClientSurveyColumns] = useState({ ClientName: true, SurveyName: true, RecipientName: false, RecipientEmail: false, SentOn: true, ResponseStatus: true, RespStartTime: false, Aging: false });
  const [projectResponseColumns, setProjectResponseColumns] = useState({ Project: true, Client: true, Practice: false, 'Survey Name': true, Customers: false, CustomerEmail: false, RespStartTime: true, CSAT: true });
  const [clientResponseColumns, setClientResponseColumns] = useState({ Client: true, 'Survey Name': true, Customers: false, CustomerEmail: false, RespStartTime: true, CSAT: true });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [currentTableType, setCurrentTableType] = useState(null);
  const [showColumnDialog, setShowColumnDialog] = useState(false);

  const handleColumnMenuOpen = (tableType) => {
    setCurrentTableType(tableType);
    setShowColumnDialog(true);
  };

  const handleColumnMenuClose = () => {
    setShowColumnDialog(false);
    setCurrentTableType(null);
  };

  const handleColumnToggle = (column) => {
    if (currentTableType === 'projectSurvey') {
      setProjectSurveyColumns(prev => ({ ...prev, [column]: !prev[column] }));
    } else if (currentTableType === 'clientSurvey') {
      setClientSurveyColumns(prev => ({ ...prev, [column]: !prev[column] }));
    } else if (currentTableType === 'projectResponse') {
      setProjectResponseColumns(prev => ({ ...prev, [column]: !prev[column] }));
    } else if (currentTableType === 'clientResponse') {
      setClientResponseColumns(prev => ({ ...prev, [column]: !prev[column] }));
    }
  };

  const getCurrentColumns = () => {
    if (currentTableType === 'projectSurvey') return projectSurveyColumns;
    if (currentTableType === 'clientSurvey') return clientSurveyColumns;
    if (currentTableType === 'projectResponse') return projectResponseColumns;
    if (currentTableType === 'clientResponse') return clientResponseColumns;
    return {};
  };

  const getColumnLabel = (columnId) => {
    const labelMap = {
      ProjectName: 'Project Name',
      ClientName: 'Client Name',
      Practice: 'Practice',
      SurveyName: 'Survey Name',
      RecipientName: 'Recipient Name',
      RecipientEmail: 'Recipient Email',
      SentOn: 'Sent On',
      ResponseStatus: 'Response Status',
      RespStartTime: 'Responded On',
      Aging: 'Days Ageing',
      Project: 'Project Name',
      Client: 'Client Name',
      'Survey Name': 'Survey Name',
      Customers: 'Sponsor',
      CustomerEmail: 'Email ID',
      CSAT: 'CSAT Score'
    };
    return labelMap[columnId] || columnId;
  };

  // Load projects, clients and surveys once
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadingProjects(true);

        const userId = sessionStorage.getItem('user_id');
        const practiceId = sessionStorage.getItem('practice_id');
        let loadedProjects = [];

        if (userId && practiceId) {
          const projectRes = await apiService.fetchData(`projects/${userId}/${practiceId}/all`);
          const projectContainer = projectRes?.data || projectRes;
          if (Array.isArray(projectContainer?.results)) {
            loadedProjects = projectContainer.results;
          } else if (Array.isArray(projectContainer)) {
            loadedProjects = projectContainer;
          }
        }

        const surveyRes = await apiService.fetchData('activesurveys');
        const surveyList = Array.isArray(surveyRes?.results)
          ? surveyRes.results.map(s => ({ SurveyID: s.ID, Name: s.Name }))
          : [];

        const clientsRes = await apiService.fetchData('clients/details');
        const clientList = Array.isArray(clientsRes?.results)
          ? clientsRes.results
          : Array.isArray(clientsRes)
            ? clientsRes
            : [];

        setProjects(loadedProjects);
        setSurveys(surveyList);
        setClients(clientList);
      } catch (err) {
        console.error('Error loading survey dashboard data:', err);
      } finally {
        setLoadingProjects(false);
      }
    };

    loadInitialData();
  }, []);

  // Load surveys sent data when tab changes to surveys sent
  useEffect(() => {
    // Reset pagination and sorting when tab or survey type changes
    setPage(0);
    setOrderBy('');
    setOrder('asc');

    if (activeTab === 0) {
      if (surveyType === 'project') {
        loadSurveysSentData();
      } else {
        loadClientSurveysData();
      }
    } else if (activeTab === 1) {
      // Set default sorting for responses received
      setOrderBy('RespStartTime');
      setOrder('desc');
      if (surveyType === 'project') {
        loadProjectResponsesData();
      } else {
        loadClientResponsesData();
      }
    }
  }, [activeTab, surveyType]);

  const loadSurveysSentData = async () => {
    try {
      setLoadingSurveysSent(true);
      const response = await apiService.fetchData('projects/surveysent');
      setSurveysSentData(response?.results || []);
    } catch (error) {
      console.error('Error loading surveys sent data:', error);
      setSurveysSentData([]);
    } finally {
      setLoadingSurveysSent(false);
    }
  };

  const loadClientSurveysData = async () => {
    try {
      setLoadingClientSurveys(true);
      const response = await apiService.fetchData('projects/surveysentclient');
      setClientSurveysData(response?.results || []);
    } catch (error) {
      console.error('Error loading client surveys data:', error);
      setClientSurveysData([]);
    } finally {
      setLoadingClientSurveys(false);
    }
  };

  const loadProjectResponsesData = async () => {
    try {
      setLoadingProjectResponses(true);
      const response = await apiService.fetchData('projects/surveycompleted');
      setProjectResponsesData(response?.results || []);
    } catch (error) {
      console.error('Error loading project responses data:', error);
      setProjectResponsesData([]);
    } finally {
      setLoadingProjectResponses(false);
    }
  };

  const loadClientResponsesData = async () => {
    try {
      setLoadingClientResponses(true);
      const response = await apiService.fetchData('projects/surveycompletedclient');
      setClientResponsesData(response?.results || []);
    } catch (error) {
      console.error('Error loading client responses data:', error);
      setClientResponsesData([]);
    } finally {
      setLoadingClientResponses(false);
    }
  };

  // Load contacts when a project is selected
  useEffect(() => {
    const loadContactsForProject = async () => {
      if (!selectedProjectId) {
        setProjectContacts([]);
        setSelectedContactId('');
        setSelectedContacts([]);
        setSelectAllContacts(false);
        return;
      }
      try {
        setLoadingContacts(true);
        const res = await apiService.fetchData(`projects/customer-contacts/${selectedProjectId}`);
        const contactsRaw = res?.contacts || res?.project_customer_contacts || [];
        const contacts = Array.isArray(contactsRaw)
          ? contactsRaw.map((c) => ({
            id: c.ID || c.id,
            name: c.FullName || c.full_name || '',
            email: c.Email || c.email || '',
          }))
          : [];
        setProjectContacts(contacts);
        setSelectedContacts([]);
        setSelectAllContacts(false);
      } catch (err) {
        console.error('Error loading project contacts:', err);
        setProjectContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    loadContactsForProject();
  }, [selectedProjectId]);

  // Load owners when a client is selected
  useEffect(() => {
    const loadOwnersForClient = async () => {
      if (!selectedClient) {
        setClientOwners([]);
        setSelectedOwnerIds([]);
        setSelectAllOwners(false);
        return;
      }
      try {
        const res = await apiService.fetchData(`clients/${selectedClient}`);
        const ownerData = Array.isArray(res?.results)
          ? res.results
          : Array.isArray(res)
            ? res
            : [];
        setClientOwners(ownerData);
        setSelectedOwnerIds([]);
        setSelectAllOwners(false);
      } catch (err) {
        console.error('Error loading client owners:', err);
        setClientOwners([]);
      }
    };

    loadOwnersForClient();
  }, [selectedClient]);

  const filteredProjects = useMemo(
    () =>
      projects.filter((p) => {
        const matchesSearch = p.Name?.toLowerCase().includes(projectSearch.toLowerCase());
        const projectTypeValue = p.Type || p.ProjectType || p.type || p.project_type || p.Category || p.category;
        const matchesType = !projectType || projectTypeValue === projectType;
        return matchesSearch && matchesType;
      }),
    [projects, projectSearch, projectType]
  );

  // Get unique project types for dropdown - check multiple possible field names
  const projectTypes = useMemo(
    () => {
      const types = projects.map(p =>
        p.Type || p.ProjectType || p.type || p.project_type || p.Category || p.category
      ).filter(Boolean);
      return [...new Set(types)];
    },
    [projects]
  );

  const filteredContacts = useMemo(
    () =>
      projectContacts.filter((c) =>
        (c.name || '').toLowerCase().includes(contactSearch.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(contactSearch.toLowerCase())
      ),
    [projectContacts, contactSearch]
  );

  // Map ownerId (string) -> { name, email, ownerId }
  const ownerMap = useMemo(
    () =>
      clientOwners.reduce((map, o) => {
        const key = String(o.ID || '');
        if (!key) return map;
        map[key] = {
          name: o.OwnerName || '',
          email: o.OwnerEmail || '',
          ownerId: o.ID || '',
        };
        return map;
      }, {}),
    [clientOwners]
  );

  // Map contactId (string) -> { name, email, contactId }
  const customerMap = useMemo(
    () =>
      projectContacts.reduce((map, c) => {
        const key = String(c.id || '');
        if (!key) return map;
        map[key] = { name: c.name || '', email: c.email || '', contactId: c.id || '' };
        return map;
      }, {}),
    [projectContacts]
  );

  // Generate stable unique IDs for each selected contact (like SendDialog)
  useEffect(() => {
    const ids = {};
    selectedContacts.forEach((contactId) => {
      ids[contactId] = uuidv4().replace(/-/g, '');
    });
    setSurveyUniqueIds(ids);
  }, [selectedContacts]);

  // Generate stable unique IDs for each selected owner (client flow)
  useEffect(() => {
    const ids = {};
    selectedOwnerIds.forEach((ownerId) => {
      ids[ownerId] = uuidv4().replace(/-/g, '');
    });
    setClientSurveyUniqueIds(ids);
  }, [selectedOwnerIds]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target)
      ) {
        setIsProjectDropdownOpen(false);
      }
      if (
        contactDropdownRef.current &&
        !contactDropdownRef.current.contains(event.target)
      ) {
        setIsContactDropdownOpen(false);
      }
      if (
        ownerDropdownRef.current &&
        !ownerDropdownRef.current.contains(event.target)
      ) {
        setIsOwnerDropdownOpen(false);
      }
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target)
      ) {
        setIsClientDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleContact = (name) => {
    setSelectedContacts((prev) => {
      const exists = prev.includes(name);
      const next = exists ? prev.filter((n) => n !== name) : [...prev, name];
      setSelectAllContacts(
        next.length > 0 &&
        filteredContacts.every((c) => next.includes(String(c.id || '')))
      );
      return next;
    });
  };

  const handleSelectAllContacts = () => {
    if (selectAllContacts) {
      setSelectedContacts([]);
      setSelectAllContacts(false);
    } else {
      const allIds = filteredContacts.map((c) => String(c.id || '')).filter(Boolean);
      setSelectedContacts(allIds);
      setSelectAllContacts(allIds.length > 0);
    }
  };

  const handleSendSurveys = async () => {
    if (!selectedProjectId || !selectedSurveyId || selectedContacts.length === 0) {
      alert('Please select a project, at least one contact, and a survey.');
      return;
    }

    const accessToken = Cookies.get('access_token');
    //const surveyBaseUrl = process.env.REACT_APP_SURVEY_BASE_URL;
    const surveyBaseUrl = window._env_?.REACT_APP_SURVEY_BASE_URL;
    const userId = sessionStorage.getItem('user_id');

    const selectedSurvey = surveys.find(
      (s) => String(s.SurveyID) === String(selectedSurveyId)
    );
    const project = projects.find(
      (p) => String(p.ID) === String(selectedProjectId)
    );

    if (!selectedSurvey || !project) {
      alert('Invalid survey or project selection.');
      return;
    }

    const projectName = project.Name || 'Unknown Project';
    const subject = `We’d love to hear from you about ${projectName} Project`;

    const emailPayloads = [];
    const surveyPayloads = [];

    selectedContacts.forEach((contactIdKey) => {
      const { name, email, contactId } = customerMap[contactIdKey] || {};
      const uniqueId = surveyUniqueIds[contactIdKey];

      if (!email || !contactId || !uniqueId) {
        return;
      }

      const surveyLink = `${surveyBaseUrl}${uniqueId}`;

      emailPayloads.push({
        endpoint: 'send_email',
        customerName: name || '',
        surveyLink,
        surveyType: selectedSurvey?.Type || 'N/A',
        projectName,
        send_to: email,
        subject,
        token: accessToken,
        templateType: 'survey',
      });

      surveyPayloads.push({
        SurveyID: parseInt(selectedSurveyId, 10),
        ProjectID: parseInt(selectedProjectId, 10),
        ContactID: parseInt(contactId, 10),
        SentBy: parseInt(userId || '1', 10),
        Subject: subject,
        Body: surveyLink,
        EmailSent: 0,
        UniqueID: uniqueId,
      });
    });

    if (emailPayloads.length === 0) {
      alert('No valid contacts to send.');
      return;
    }

    try {
      setIsSending(true);
      for (let i = 0; i < emailPayloads.length; i += 1) {
        const ep = emailPayloads[i];
        const sp = surveyPayloads[i];

        try {
          await apiService.sendEmail(
            ep.endpoint,
            ep.customerName,
            ep.surveyLink,
            ep.surveyType,
            ep.projectName,
            ep.send_to,
            ep.subject,
            ep.token,
            ep.templateType
          );
          sp.EmailSent = 1;
          await apiService.postData('send_survey', sp, accessToken);
        } catch (err) {
          console.error(`Failed for ${ep.customerName}:`, err);
          try {
            await apiService.postData('send_survey', sp, accessToken);
          } catch (e) {
            console.error('Survey log failed:', e);
          }
        }
      }

      setSnackbarMessage('Email sent successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh surveys sent data
      if (activeTab === 0) {
        await loadSurveysSentData();
      }

      // Reset selections after successful send
      setSelectedProjectId('');
      setProjectContacts([]);
      setSelectedContactId('');
      setSelectedContacts([]);
      setSelectAllContacts(false);
      setSelectedSurveyId('');
      setProjectSearch('');
      setContactSearch('');
    } catch (err) {
      console.error('Batch error:', err);
      setSnackbarMessage('Failed to send one or more emails.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendClientSurveys = async () => {
    if (!selectedClient || !clientSurveyId || selectedOwnerIds.length === 0) {
      setSnackbarMessage('Please select client, at least one contact, and a survey.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const accessToken = Cookies.get('access_token');
    //const surveyBaseUrl = process.env.REACT_APP_SURVEY_BASE_URL;
    const surveyBaseUrl = window._env_?.REACT_APP_SURVEY_BASE_URL;
    const userId = sessionStorage.getItem('user_id');

    const selectedSurvey = surveys.find(
      (s) => String(s.SurveyID) === String(clientSurveyId)
    );

    if (!selectedSurvey) {
      setSnackbarMessage('Invalid survey selection.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const subjectLine = `We’d Appreciate Your Feedback on ${selectedSurvey.Name} Survey`;

    const payloads = [];

    selectedOwnerIds.forEach((ownerIdKey) => {
      const { name, email, ownerId } = ownerMap[ownerIdKey] || {};
      const uniqueId = clientSurveyUniqueIds[ownerIdKey];

      if (!email || !ownerId || !uniqueId) {
        return;
      }

      const surveyLink = `${surveyBaseUrl}${uniqueId}`;

      const surveyPayload = {
        SurveyID: parseInt(clientSurveyId, 10),
        ClientID: parseInt(ownerId, 10) || 1,
        SentBy: parseInt(userId || '1', 10),
        Subject: subjectLine,
        Body: surveyLink,
        EmailSent: 0,
        UniqueID: uniqueId,
      };

      payloads.push({
        surveyPayload,
        email,
        customerName: name || '',
        surveyLink,
        surveyName: selectedSurvey.Name,
      });
    });

    if (payloads.length === 0) {
      setSnackbarMessage('No valid contacts to send.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsSending(true);
      for (let i = 0; i < payloads.length; i += 1) {
        const { surveyPayload, email, customerName, surveyLink, surveyName } =
          payloads[i];
        const recipientName = customerName || selectedClient || 'Valued Customer';

        try {
          await apiService.sendEmail(
            'send_email',
            recipientName,
            surveyLink,
            '',
            '',
            email,
            subjectLine,
            accessToken,
            'ClientSurvey',
            '',
            '',
            '',
            'CriticalRiver Technologies',
            surveyName
          );
          surveyPayload.EmailSent = 1;
          await apiService.postData('send_survey', surveyPayload, accessToken);
        } catch (err) {
          console.error(`Error sending client survey to ${email}:`, err);
          try {
            await apiService.postData('send_survey', surveyPayload, accessToken);
          } catch (e) {
            console.error('Survey log failed:', e);
          }
        }
      }

      setSnackbarMessage('Email sent successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh client surveys data
      if (activeTab === 0) {
        await loadClientSurveysData();
      }

      // Reset client selections
      setSelectedClient('');
      setClientOwners([]);
      setSelectedOwnerIds([]);
      setSelectAllOwners(false);
      setClientSurveyId('');
      setOwnerSearch('');
    } catch (err) {
      console.error('Client batch error:', err);
      setSnackbarMessage('Failed to send one or more emails.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleClientSurveyClick = () => {
    navigate('/surveys');
  };

  const handleSurveyNameClick = (surveyId, clientId, responseDetails) => {
    setSelectedSurveyResponse({ surveyId, clientId, responseDetails });
    setOpenResponseDialog(true);
  };

  const handleCloseResponseDialog = () => {
    setOpenResponseDialog(false);
    setSelectedSurveyResponse({ surveyId: null, clientId: null, responseDetails: null });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSurveyTypeChange = (event, newType) => {
    if (newType !== null) {
      setSurveyType(newType);
      setPage(0);
      // Clear project/client selections when switching survey type
      setSelectedProjectId('');
      setSelectedClient('');
      setProjectContacts([]);
      setClientOwners([]);
      setSelectedContacts([]);
      setSelectedOwnerIds([]);
      setSelectAllContacts(false);
      setSelectAllOwners(false);
      setSelectedSurveyId('');
      setClientSurveyId('');
      setProjectSearch('');
      setClientSearch('');
      setContactSearch('');
      setOwnerSearch('');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    setPage(0);
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const calculateAging = (sentDate, responseStatus, respondedDate) => {
    const sent = new Date(sentDate);
    if (!respondedDate) {
      const today = new Date();
      const diffTime = Math.abs(today - sent);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} days`;
    }
    const responded = new Date(respondedDate);
    const diffTime = Math.abs(responded - sent);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  // Calculate KPIs for surveys sent
  const calculateSentKPIs = (data) => {
    const total = data.length;
    const responded = data.filter(item =>
      item.ResponseStatus === 'Completed' ||
      item.ResponseStatus === 'Complete' ||
      item.RespStartTime
    ).length;
    const pending = total - responded;
    const responseRate = total > 0 ? ((responded / total) * 100).toFixed(1) : '0.0';

    return { total, responded, pending, responseRate };
  };

  // Calculate KPIs for responses received
  const calculateResponseKPIs = (data) => {
    const total = data.length;
    const scores = data.filter(item => item.CSAT && !isNaN(parseFloat(item.CSAT))).map(item => parseFloat(item.CSAT));
    const avgCSAT = scores.length > 0 ? (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1) : '0.0';
    const lowSatisfaction = scores.filter(score => score >= 0 && score < 2.5).length;
    const mediumSatisfaction = scores.filter(score => score >= 2.5 && score < 4).length;
    const highSatisfaction = scores.filter(score => score >= 4 && score <= 5).length;

    return { total, avgCSAT, lowSatisfaction, mediumSatisfaction, highSatisfaction };
  };

  // Render KPI cards for surveys sent
  const renderSentKPIs = (data, loading = false) => {
    if (loading) {
      return (
        <div className="kpi-container">
          <div className="kpi-grid">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            ))}
          </div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return null;
    }

    const kpis = calculateSentKPIs(data);

    return (
      <div className="kpi-container">
        <div className="kpi-grid">
          <div className="kpi-card kpi-card-blue">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.total}</div>
              <div className="kpi-label">Total Surveys Sent</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-green">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.responded}</div>
              <div className="kpi-label">Responses Received</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-orange">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.pending}</div>
              <div className="kpi-label">Pending Responses</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-purple">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.responseRate}%</div>
              <div className="kpi-label">Response Rate</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render KPI cards for responses received
  const renderResponseKPIs = (data, loading = false) => {
    if (loading) {
      return (
        <div className="kpi-container">
          <div className="kpi-grid">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            ))}
          </div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return null;
    }

    const kpis = calculateResponseKPIs(data);

    return (
      <div className="kpi-container">
        <div className="kpi-grid">
          <div className="kpi-card kpi-card-blue">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.total}</div>
              <div className="kpi-label">Total Responses</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-green">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.avgCSAT}</div>
              <div className="kpi-label">Average CSAT</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-red">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.lowSatisfaction}</div>
              <div className="kpi-label">Low (0-2.5)</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-orange">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.mediumSatisfaction}</div>
              <div className="kpi-label">Medium (2.5-4)</div>
            </div>
          </div>
          <div className="kpi-card kpi-card-green">
            <div className="kpi-card-content">
              <div className="kpi-value">{kpis.highSatisfaction}</div>
              <div className="kpi-label">High (4-5)</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sortData = (data) => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'SentOn' || orderBy === 'RespStartTime') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (orderBy === 'CSAT') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter data based on search query
  const filterData = (data, searchQuery) => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(item =>
      Object.values(item).some(value =>
        value && value.toString().toLowerCase().includes(query)
      )
    );
  };

  // Export handlers
  const handleExportClick = (event, data, columns, filename) => {
    setExportAnchorEl(event.currentTarget);
    setCurrentExportData({ data, columns, filename });
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Export to CSV
  const exportToCSV = (data, columns, filename) => {
    if (!data || data.length === 0) {
      setSnackbarMessage('No data to export');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    // Filter data to include only columns visible in the table
    const filteredData = data.map(row => {
      const filteredRow = {};
      columns.forEach(col => {
        filteredRow[col.label] = row[col.id] || 'N/A';
      });
      return filteredRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    setSnackbarMessage('Data exported successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Export to PDF
  const exportToPDF = (data, columns, filename) => {
    if (!data || data.length === 0) {
      setSnackbarMessage('No data to export');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const doc = new jsPDF('l', 'mm', 'a4');
    autoTable(doc, {
      head: [columns.map(col => col.label)],
      body: data.map(row => columns.map(col => row[col.id] || 'N/A')),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [1, 62, 125] }
    });
    doc.save(`${filename}.pdf`);
    setSnackbarMessage('Data exported successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleExportCSV = () => {
    exportToCSV(currentExportData.data, currentExportData.columns, currentExportData.filename);
    handleExportClose();
  };

  const handleExportPDF = () => {
    exportToPDF(currentExportData.data, currentExportData.columns, currentExportData.filename);
    handleExportClose();
  };

  const renderClientSurveysTable = () => {
    if (loadingClientSurveys) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (clientSurveysData.length === 0) {
      return <Typography>No client surveys data found.</Typography>;
    }

    const filteredData = filterData(clientSurveysData, clientSurveySearch);
    const sortedData = sortData(filteredData);
    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const columns = [
      { id: 'ClientName', label: 'Client Name' },
      { id: 'SurveyName', label: 'Survey Name' },
      { id: 'RecipientName', label: 'Recipient Name' },
      { id: 'RecipientEmail', label: 'Recipient Email' },
      { id: 'SentOn', label: 'Sent On' },
      { id: 'ResponseStatus', label: 'Response Status' },
      { id: 'RespStartTime', label: 'Responded On' },
      { id: 'Aging', label: 'Days Ageing' }
    ];

    return (
      <Box>
        <Box sx={{ width: '100%' }}>
          {renderSentKPIs(filteredData, loadingClientSurveys)}
        </Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={clientSurveySearch}
            onChange={(e) => setClientSurveySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={() => handleColumnMenuOpen('clientSurvey')}
              size="small"
            >
              Column Options
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={(e) => handleExportClick(e, sortedData, columns.filter(c => clientSurveyColumns[c.id]), 'client-surveys-sent')}
              size="small"
            >
              Export
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={handleChangeRowsPerPage}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {columns.filter(c => clientSurveyColumns[c.id]).map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      minWidth: column.id === 'RecipientEmail' ? 200 :
                        column.id === 'ClientName' ? 150 :
                          column.id === 'SurveyName' ? 150 :
                            column.id === 'RecipientName' ? 150 :
                              column.id === 'SentOn' ? 120 :
                                column.id === 'ResponseStatus' ? 130 :
                                  column.id === 'RespStartTime' ? 120 :
                                    column.id === 'Aging' ? 100 : 120,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {column.id !== 'Aging' ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.filter(c => clientSurveyColumns[c.id]).length} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {clientSurveySearch ? 'No search results found' : 'No data available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((survey) => (
                  <TableRow
                    key={survey.ID}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    {clientSurveyColumns.ClientName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.ClientName || 'N/A'}>{survey.ClientName || 'N/A'}</TableCell>}
                    {clientSurveyColumns.SurveyName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.SurveyName || 'N/A'}>{survey.SurveyName || 'N/A'}</TableCell>}
                    {clientSurveyColumns.RecipientName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.RecipientName || 'N/A'}>{survey.RecipientName || 'N/A'}</TableCell>}
                    {clientSurveyColumns.RecipientEmail && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} title={survey.RecipientEmail || 'N/A'}>{survey.RecipientEmail || 'N/A'}</TableCell>}
                    {clientSurveyColumns.SentOn && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{new Date(survey.SentOn).toLocaleDateString()}</TableCell>}
                    {clientSurveyColumns.ResponseStatus && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{survey.ResponseStatus || 'N/A'}</TableCell>}
                    {clientSurveyColumns.RespStartTime && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{survey.RespStartTime ? new Date(survey.RespStartTime).toLocaleDateString() : 'N/A'}</TableCell>}
                    {clientSurveyColumns.Aging && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{calculateAging(survey.SentOn, survey.ResponseStatus, survey.RespStartTime)}</TableCell>}
                  </TableRow>
                )))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>
    );
  };

  const renderSurveysSentTable = () => {
    if (loadingSurveysSent) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (surveysSentData.length === 0) {
      return <Typography>No surveys sent data found.</Typography>;
    }

    const filteredData = filterData(surveysSentData, projectSurveySearch);
    const sortedData = sortData(filteredData);
    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const columns = [
      { id: 'ProjectName', label: 'Project Name' },
      { id: 'ClientName', label: 'Client Name' },
      { id: 'Practice', label: 'Practice' },
      { id: 'SurveyName', label: 'Survey Name' },
      { id: 'RecipientName', label: 'Recipient Name' },
      { id: 'RecipientEmail', label: 'Recipient Email' },
      { id: 'SentOn', label: 'Sent On' },
      { id: 'ResponseStatus', label: 'Response Status' },
      { id: 'RespStartTime', label: 'Responded On' },
      { id: 'Aging', label: 'Days Ageing' }
    ];

    return (
      <Box>
        <Box sx={{ width: '100%' }}>
          {renderSentKPIs(filteredData, loadingSurveysSent)}
        </Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={projectSurveySearch}
            onChange={(e) => setProjectSurveySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={() => handleColumnMenuOpen('projectSurvey')}
              size="small"
            >
              Column Options
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={(e) => handleExportClick(e, sortedData, columns, 'project-surveys-sent')}
              size="small"
            >
              Export
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={handleChangeRowsPerPage}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {columns.filter(c => projectSurveyColumns[c.id]).map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      minWidth: column.id === 'RecipientEmail' ? 200 :
                        column.id === 'ProjectName' ? 150 :
                          column.id === 'ClientName' ? 150 :
                            column.id === 'SurveyName' ? 150 :
                              column.id === 'RecipientName' ? 150 :
                                column.id === 'SentOn' ? 120 :
                                  column.id === 'ResponseStatus' ? 130 :
                                    column.id === 'RespStartTime' ? 120 :
                                      column.id === 'Aging' ? 100 : 120,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {column.id !== 'Aging' ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.filter(c => projectSurveyColumns[c.id]).length} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {projectSurveySearch ? 'No search results found' : 'No data available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((survey, index) => (
                  <TableRow
                    key={survey.ID}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    {projectSurveyColumns.ProjectName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.ProjectName || 'N/A'}>{survey.ProjectName || 'N/A'}</TableCell>}
                    {projectSurveyColumns.ClientName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.ClientName || 'N/A'}>{survey.ClientName || 'N/A'}</TableCell>}
                    {projectSurveyColumns.Practice && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.Practice || 'N/A'}>{survey.Practice || 'N/A'}</TableCell>}
                    {projectSurveyColumns.SurveyName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.SurveyName || 'N/A'}>{survey.SurveyName || 'N/A'}</TableCell>}
                    {projectSurveyColumns.RecipientName && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={survey.RecipientName || 'N/A'}>{survey.RecipientName || 'N/A'}</TableCell>}
                    {projectSurveyColumns.RecipientEmail && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} title={survey.RecipientEmail || 'N/A'}>{survey.RecipientEmail || 'N/A'}</TableCell>}
                    {projectSurveyColumns.SentOn && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{new Date(survey.SentOn).toLocaleDateString()}</TableCell>}
                    {projectSurveyColumns.ResponseStatus && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{survey.ResponseStatus || 'N/A'}</TableCell>}
                    {projectSurveyColumns.RespStartTime && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{survey.RespStartTime ? new Date(survey.RespStartTime).toLocaleDateString() : 'N/A'}</TableCell>}
                    {projectSurveyColumns.Aging && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{calculateAging(survey.SentOn, survey.ResponseStatus, survey.RespStartTime)}</TableCell>}
                  </TableRow>
                )))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>
    );
  };

  const renderProjectResponsesTable = () => {
    if (loadingProjectResponses) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (projectResponsesData.length === 0) {
      return <Typography>No project responses data found.</Typography>;
    }

    const filteredData = filterData(projectResponsesData, projectResponseSearch);
    const sortedData = sortData(filteredData);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const columns = [
      { id: 'Project', label: 'Project Name' },
      { id: 'Client', label: 'Client Name' },
      { id: 'Practice', label: 'Practice' },
      { id: 'Survey Name', label: 'Survey Name' },
      { id: 'Customers', label: 'Sponsor' },
      { id: 'CustomerEmail', label: 'Email ID' },
      { id: 'RespStartTime', label: 'Response Date' },
      { id: 'CSAT', label: 'CSAT Score' }
    ];

    return (
      <Box>
        <Box sx={{ width: '100%' }}>
          {renderResponseKPIs(filteredData, loadingProjectResponses)}
        </Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={projectResponseSearch}
            onChange={(e) => setProjectResponseSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={() => handleColumnMenuOpen('projectResponse')}
              size="small"
            >
              Column Options
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={(e) => handleExportClick(e, sortedData, columns, 'project-responses-received')}
              size="small"
            >
              Export
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={handleChangeRowsPerPage}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {columns.filter(c => projectResponseColumns[c.id]).map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      minWidth: column.id === 'CustomerEmail' ? 200 :
                        column.id === 'Project' ? 150 :
                          column.id === 'Client' ? 150 :
                            column.id === 'Survey Name' ? 150 :
                              column.id === 'Customers' ? 150 :
                                column.id === 'RespStartTime' ? 120 :
                                  column.id === 'CSAT' ? 100 : 120,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((response, index) => (
                <TableRow
                  key={`${response.SurveyID || 'survey'}-${response.CustomerEmail || 'email'}-${index}`}
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    '&:hover': { backgroundColor: '#f0f0f0' }
                  }}
                >
                  {projectResponseColumns.Project && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response.Project || 'N/A'}>{response.Project || 'N/A'}</TableCell>}
                  {projectResponseColumns.Client && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response.Client || 'N/A'}>{response.Client || 'N/A'}</TableCell>}
                  {projectResponseColumns.Practice && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response.Practice || 'N/A'}>{response.Practice || 'N/A'}</TableCell>}
                  {projectResponseColumns['Survey Name'] && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response['Survey Name'] || 'N/A'}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleSurveyNameClick(response.SurveyID, response.ClientID, {
                        projectName: response.Project,
                        clientName: response.Client,
                        customerName: response.Customers,
                        customerEmail: response.CustomerEmail,
                        surveyType: 'project'
                      })}
                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#013E7D' }}
                    >
                      {response['Survey Name'] || 'N/A'}
                    </Link>
                  </TableCell>}
                  {projectResponseColumns.Customers && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response.Customers || 'N/A'}>{response.Customers || 'N/A'}</TableCell>}
                  {projectResponseColumns.CustomerEmail && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} title={response.CustomerEmail || 'N/A'}>{response.CustomerEmail || 'N/A'}</TableCell>}
                  {projectResponseColumns.RespStartTime && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{response.RespStartTime ? new Date(response.RespStartTime).toLocaleDateString() : 'N/A'}</TableCell>}
                  {projectResponseColumns.CSAT && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', fontWeight: 600, color: response.CSAT >= 4 ? '#4caf50' : response.CSAT >= 3 ? '#ff9800' : '#f44336' }}>{response.CSAT || 'N/A'}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>
    );
  };

  const renderClientResponsesTable = () => {
    if (loadingClientResponses) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (clientResponsesData.length === 0) {
      return <Typography>No client responses data found.</Typography>;
    }

    const filteredData = filterData(clientResponsesData, clientResponseSearch);
    const sortedData = sortData(filteredData);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const columns = [
      { id: 'Client', label: 'Client Name' },
      { id: 'Survey Name', label: 'Survey Name' },
      { id: 'Customers', label: 'Sponsor' },
      { id: 'CustomerEmail', label: 'Email ID' },
      { id: 'RespStartTime', label: 'Response Date' },
      { id: 'CSAT', label: 'CSAT Score' }
    ];

    return (
      <Box>
        <Box sx={{ width: '100%' }}>
          {renderResponseKPIs(filteredData, loadingClientResponses)}
        </Box>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={clientResponseSearch}
            onChange={(e) => setClientResponseSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={() => handleColumnMenuOpen('clientResponse')}
              size="small"
            >
              Column Options
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={(e) => handleExportClick(e, sortedData, columns, 'client-responses-received')}
              size="small"
            >
              Export
            </Button>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={handleChangeRowsPerPage}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {columns.filter(c => clientResponseColumns[c.id]).map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      minWidth: column.id === 'CustomerEmail' ? 200 :
                        column.id === 'Client' ? 150 :
                          column.id === 'Survey Name' ? 150 :
                            column.id === 'Customers' ? 150 :
                              column.id === 'RespStartTime' ? 120 :
                                column.id === 'CSAT' ? 100 : 120,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((response, index) => (
                <TableRow
                  key={`${response.SurveyID || 'survey'}-${response.CustomerEmail || 'email'}-${index}`}
                  sx={{
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    '&:hover': { backgroundColor: '#f0f0f0' }
                  }}
                >
                  {clientResponseColumns.Client && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response.Client || 'N/A'}>{response.Client || 'N/A'}</TableCell>}
                  {clientResponseColumns['Survey Name'] && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response['Survey Name'] || 'N/A'}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleSurveyNameClick(response.SurveyID, response.ClientID, {
                        clientName: response.Client,
                        customerName: response.Customers,
                        customerEmail: response.CustomerEmail,
                        surveyType: 'client'
                      })}
                      sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#013E7D' }}
                    >
                      {response['Survey Name'] || 'N/A'}
                    </Link>
                  </TableCell>}
                  {clientResponseColumns.Customers && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }} title={response.Customers || 'N/A'}>{response.Customers || 'N/A'}</TableCell>}
                  {clientResponseColumns.CustomerEmail && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }} title={response.CustomerEmail || 'N/A'}>{response.CustomerEmail || 'N/A'}</TableCell>}
                  {clientResponseColumns.RespStartTime && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{response.RespStartTime ? new Date(response.RespStartTime).toLocaleDateString() : 'N/A'}</TableCell>}
                  {clientResponseColumns.CSAT && <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap', fontWeight: 600, color: response.CSAT >= 4 ? '#4caf50' : response.CSAT >= 3 ? '#ff9800' : '#f44336' }}>{response.CSAT || 'N/A'}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>
    );
  };

  const renderResponsesReceivedTable = () => {
    return (
      <Box>
        {/* Toggle for Project/Client Survey */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={surveyType}
            exclusive
            onChange={handleSurveyTypeChange}
            color="primary"
          >
            <ToggleButton value="project" sx={{ px: 3 }}>
              Project Survey
            </ToggleButton>
            <ToggleButton value="client" sx={{ px: 3 }}>
              Client Survey
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {surveyType === 'project' ? renderProjectResponsesTable() : renderClientResponsesTable()}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#013E7D' }}>
          Survey Dashboard
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showSendSurvey}
              onChange={(e) => {
                const newValue = e.target.checked;
                setShowSendSurvey(newValue);
                sessionStorage.setItem('showSendSurvey', JSON.stringify(newValue));
              }}
              color="primary"
            />
          }
          label={showSendSurvey ? "Hide Send Survey" : "Send Survey"}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {showSendSurvey && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            mb: 4,
          }}
        >
          {/* Project Survey block with three dropdowns */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #E3F2FD 0%, #FFFFFF 60%)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Send Survey for Project
            </Typography>

            {/* Project Type Filter */}
            <Box sx={{ mb: 2, position: 'relative' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Project Type (Optional)
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
              >
                <select
                  value={projectType}
                  onChange={(e) => {
                    setProjectType(e.target.value);
                    setSelectedProjectId('');
                    setProjectContacts([]);
                    setSelectedContacts([]);
                    setSelectAllContacts(false);
                    setSelectedSurveyId('');
                    setProjectSearch('');
                    setContactSearch('');
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    paddingRight: '30px',
                    paddingLeft: '4px',
                    fontSize: '14px',
                    color: projectType ? '#000' : '#666',
                  }}
                >
                  <option value="" style={{ color: '#666' }}>Select Project Type</option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type} style={{ color: '#000' }}>
                      {type}
                    </option>
                  ))}
                </select>
                <ArrowDropDownIcon style={{ color: '#666', position: 'absolute', right: '8px', pointerEvents: 'none' }} />
              </div>
            </Box>

            {/* 1. Select Project (custom dropdown with search inside) */}
            <Box sx={{ mb: 2, position: 'relative' }} ref={projectDropdownRef}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Select Project
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setIsProjectDropdownOpen((prev) => !prev)}
              >
                <span>
                  {loadingProjects
                    ? 'Loading projects...'
                    : selectedProjectId
                      ? projects.find((p) => String(p.ID) === String(selectedProjectId))?.Name ||
                      'Choose a Project'
                      : 'Choose a Project'}
                </span>
                <ArrowDropDownIcon style={{ color: '#666' }} />
              </div>
              {isProjectDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #A9B9C9',
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 260,
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search project..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: 'none',
                      borderBottom: '1px solid #E0E0E0',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                  {filteredProjects.map((p) => (
                    <div
                      key={p.ID}
                      onClick={() => {
                        setSelectedProjectId(p.ID);
                        setIsProjectDropdownOpen(false);
                      }}
                      style={{
                        padding: '6px 8px',
                        fontSize: 14,
                        cursor: 'pointer',
                        backgroundColor:
                          String(p.ID) === String(selectedProjectId) ? '#E3F2FD' : '#fff',
                      }}
                    >
                      {p.Name}
                    </div>
                  ))}
                  {filteredProjects.length === 0 && (
                    <div style={{ padding: '6px 8px', fontSize: 14, color: '#777' }}>
                      No projects found
                    </div>
                  )}
                </div>
              )}
            </Box>

            {/* 2. Select Customer Contacts (custom multi-select with search inside) */}
            <Box sx={{ mb: 2, position: 'relative' }} ref={contactDropdownRef}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Select Customer Contacts
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: selectedProjectId ? 'pointer' : 'not-allowed',
                  backgroundColor: selectedProjectId ? '#fff' : '#f5f5f5',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => {
                  if (selectedProjectId) {
                    setIsContactDropdownOpen((prev) => !prev);
                  }
                }}
              >
                <span>
                  {!selectedProjectId
                    ? 'Select Name(s)'
                    : loadingContacts
                      ? 'Loading contacts...'
                      : selectedContacts.length > 0
                        ? `${selectedContacts.length} contact(s) selected`
                        : filteredContacts.length === 0
                          ? 'No contacts found'
                          : 'Choose contacts'}
                </span>
                <ArrowDropDownIcon style={{ color: selectedProjectId ? '#666' : '#ccc' }} />
              </div>
              {isContactDropdownOpen && selectedProjectId && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #A9B9C9',
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 260,
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search contact..."
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: 'none',
                      borderBottom: '1px solid #E0E0E0',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                  <div
                    onClick={handleSelectAllContacts}
                    style={{
                      padding: '6px 8px',
                      fontSize: 14,
                      cursor: 'pointer',
                      borderBottom: '1px solid #E0E0E0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectAllContacts}
                      readOnly
                    />
                    <span>Select All</span>
                  </div>
                  {filteredContacts.map((c) => {
                    const idKey = String(c.id || '');
                    const checked = selectedContacts.includes(idKey);
                    return (
                      <div
                        key={c.id}
                        onClick={() => toggleContact(idKey)}
                        style={{
                          padding: '6px 8px',
                          fontSize: 14,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          backgroundColor: checked ? '#E3F2FD' : '#fff',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          readOnly
                        />
                        <span>
                          {c.name || ''} {c.email ? `- ${c.email}` : ''}
                        </span>
                      </div>
                    );
                  })}
                  {filteredContacts.length === 0 && (
                    <div style={{ padding: '6px 8px', fontSize: 14, color: '#777' }}>
                      No contacts found
                    </div>
                  )}
                </div>
              )}
            </Box>

            {/* 3. Select Survey */}
            <Box sx={{ mb: 2, position: 'relative' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Select Survey
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
              >
                <select
                  className="Project-filter-select"
                  value={selectedSurveyId}
                  onChange={(e) => setSelectedSurveyId(e.target.value)}
                  disabled={surveys.length === 0}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: surveys.length === 0 ? 'not-allowed' : 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    paddingRight: '30px',
                    paddingLeft: '4px',
                    fontSize: '14px',
                    color: selectedSurveyId ? '#000' : '#666',
                    backgroundImage: 'none',
                  }}
                >
                  <option value="" style={{ color: '#666' }}>Select Survey Template</option>
                  {surveys.map((s) => (
                    <option key={s.SurveyID} value={s.SurveyID} style={{ color: '#000' }}>
                      {s.Name}
                    </option>
                  ))}
                </select>
                <ArrowDropDownIcon style={{ color: surveys.length === 0 ? '#ccc' : '#666', position: 'absolute', right: '8px', pointerEvents: 'none' }} />
              </div>
            </Box>

            {/* Action button – uses same APIs/payload as Projects SendDialog */}
            <Button
              variant="contained"
              color="primary"
              disabled={
                !selectedProjectId ||
                selectedContacts.length === 0 ||
                !selectedSurveyId ||
                surveys.length === 0 ||
                isSending
              }
              onClick={handleSendSurveys}
              sx={{ textTransform: 'none', fontWeight: 600, mt: 1 }}
            >
              {isSending ? 'Sending...' : 'Send Survey'}
            </Button>
          </Box>

          {/* Client Survey block with three dropdowns */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #FFF3E0 0%, #FFFFFF 60%)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Send Survey to Client
            </Typography>

            {/* 1. Select Client (custom dropdown with search) */}
            <Box sx={{ mb: 2, position: 'relative' }} ref={clientDropdownRef}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Select Client
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => setIsClientDropdownOpen((prev) => !prev)}
              >
                <span>{selectedClient || 'Choose a Client'}</span>
                <ArrowDropDownIcon style={{ color: '#666' }} />
              </div>

              {isClientDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #A9B9C9',
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 260,
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search client..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: 'none',
                      borderBottom: '1px solid #E0E0E0',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                  {clients
                    .filter((c) =>
                      c.Name?.toLowerCase().includes(clientSearch.toLowerCase())
                    )
                    .map((c) => (
                      <div
                        key={c.Name}
                        onClick={() => {
                          setSelectedClient(c.Name);
                          setIsClientDropdownOpen(false);
                          setClientSearch(''); // clears search after selection
                        }}
                        style={{
                          padding: '6px 8px',
                          fontSize: 14,
                          cursor: 'pointer',
                          backgroundColor: c.Name === selectedClient ? '#E3F2FD' : '#fff',
                        }}
                      >
                        {c.Name}
                      </div>
                    ))}
                  {clients.filter((c) =>
                    c.Name?.toLowerCase().includes(clientSearch.toLowerCase())
                  ).length === 0 && (
                      <div style={{ padding: '6px 8px', fontSize: 14, color: '#777' }}>
                        No clients found
                      </div>
                    )}
                </div>
              )}
            </Box>

            {/* 2. Select Client Contacts (owners) */}
            <Box sx={{ mb: 2, position: 'relative' }} ref={ownerDropdownRef}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Select Client Contacts
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: selectedClient ? 'pointer' : 'not-allowed',
                  backgroundColor: selectedClient ? '#fff' : '#f5f5f5',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() => {
                  if (selectedClient) {
                    setIsOwnerDropdownOpen((prev) => !prev);
                  }
                }}
              >
                <span>
                  {!selectedClient
                    ? 'Select Name(s)'
                    : clientOwners.length === 0
                      ? 'No contacts found'
                      : selectedOwnerIds.length > 0
                        ? `${selectedOwnerIds.length} contact(s) selected`
                        : 'Choose contacts'}
                </span>
                <ArrowDropDownIcon style={{ color: selectedClient ? '#666' : '#ccc' }} />
              </div>
              {isOwnerDropdownOpen && selectedClient && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #A9B9C9',
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 260,
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search contact..."
                    value={ownerSearch}
                    onChange={(e) => setOwnerSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: 'none',
                      borderBottom: '1px solid #E0E0E0',
                      fontSize: 14,
                      boxSizing: 'border-box',
                    }}
                  />
                  <div
                    onClick={() => {
                      if (selectAllOwners) {
                        setSelectedOwnerIds([]);
                        setSelectAllOwners(false);
                      } else {
                        const allIds = clientOwners
                          .map((o) => String(o.ID || ''))
                          .filter(Boolean);
                        setSelectedOwnerIds(allIds);
                        setSelectAllOwners(allIds.length > 0);
                      }
                    }}
                    style={{
                      padding: '6px 8px',
                      fontSize: 14,
                      cursor: 'pointer',
                      borderBottom: '1px solid #E0E0E0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <input type="checkbox" checked={selectAllOwners} readOnly />
                    <span>Select All</span>
                  </div>
                  {clientOwners
                    .filter((owner) => {
                      const searchLower = ownerSearch.toLowerCase();
                      return (
                        (owner.OwnerName || '').toLowerCase().includes(searchLower) ||
                        (owner.OwnerEmail || '').toLowerCase().includes(searchLower)
                      );
                    })
                    .map((owner) => {
                      const idKey = String(owner.ID || '');
                      const checked = selectedOwnerIds.includes(idKey);
                      return (
                        <div
                          key={idKey}
                          onClick={() => {
                            setSelectedOwnerIds((prev) => {
                              const exists = prev.includes(idKey);
                              const next = exists
                                ? prev.filter((id) => id !== idKey)
                                : [...prev, idKey];
                              setSelectAllOwners(
                                next.length > 0 &&
                                clientOwners.every((o) => next.includes(String(o.ID || '')))
                              );
                              return next;
                            });
                          }}
                          style={{
                            padding: '6px 8px',
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            backgroundColor: checked ? '#E3F2FD' : '#fff',
                          }}
                        >
                          <input type="checkbox" checked={checked} readOnly />
                          <span>
                            {owner.OwnerName}{' '}
                            {owner.OwnerEmail ? `- ${owner.OwnerEmail}` : ''}
                          </span>
                        </div>
                      );
                    })}
                  {clientOwners.length === 0 && (
                    <div style={{ padding: '6px 8px', fontSize: 14, color: '#777' }}>
                      No contacts found
                    </div>
                  )}
                </div>
              )}
            </Box>

            {/* 3. Select Survey */}
            <Box sx={{ mb: 2, position: 'relative' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Select Survey
              </Typography>
              <div
                style={{
                  width: '100%',
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #A9B9C9',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  height: '38px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
              >
                <select
                  className="Project-filter-select"
                  value={clientSurveyId}
                  onChange={(e) => setClientSurveyId(e.target.value)}
                  disabled={surveys.length === 0}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: surveys.length === 0 ? 'not-allowed' : 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    paddingRight: '30px',
                    paddingLeft: '4px',
                    fontSize: '14px',
                    color: clientSurveyId ? '#000' : '#666',
                    backgroundImage: 'none',
                  }}
                >
                  <option value="" style={{ color: '#666' }}>Select Survey Template</option>
                  {surveys.map((s) => (
                    <option key={s.SurveyID} value={s.SurveyID} style={{ color: '#000' }}>
                      {s.Name}
                    </option>
                  ))}
                </select>
                <ArrowDropDownIcon style={{ color: surveys.length === 0 ? '#ccc' : '#666', position: 'absolute', right: '8px', pointerEvents: 'none' }} />
              </div>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              disabled={
                !selectedClient ||
                selectedOwnerIds.length === 0 ||
                !clientSurveyId ||
                surveys.length === 0 ||
                isSending
              }
              onClick={handleSendClientSurveys}
              sx={{ textTransform: 'none', fontWeight: 600, mt: 1 }}
            >
              {isSending ? 'Sending...' : 'Send Survey'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Tabs Section */}
      <Box sx={{ mt: showSendSurvey ? 4 : 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Surveys Sent" />
          <Tab label="Responses Received" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Box>
              {/* Toggle for Project/Client Survey */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <ToggleButtonGroup
                  value={surveyType}
                  exclusive
                  onChange={handleSurveyTypeChange}
                  color="primary"
                >
                  <ToggleButton value="project" sx={{ px: 3 }}>
                    Project Survey
                  </ToggleButton>
                  <ToggleButton value="client" sx={{ px: 3 }}>
                    Client Survey
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {surveyType === 'project' ? renderSurveysSentTable() : renderClientSurveysTable()}
            </Box>
          )}
          {activeTab === 1 && renderResponsesReceivedTable()}
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        ContentProps={{
          sx: {
            backgroundColor: snackbarSeverity === 'success' ? '#4caf50' : '#f44336',
          },
        }}
      />

      <Dialog open={showColumnDialog} onClose={handleColumnMenuClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#013E7D', color: '#fff', fontWeight: 600 }}>
          Column Options
          <IconButton onClick={handleColumnMenuClose} sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Column Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Visible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(getCurrentColumns()).map((column) => (
                <TableRow key={column}>
                  <TableCell>{getColumnLabel(column)}</TableCell>
                  <TableCell align="right">
                    <Checkbox checked={getCurrentColumns()[column]} onChange={() => handleColumnToggle(column)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleColumnMenuClose} variant="outlined">Cancel</Button>
          <Button onClick={handleColumnMenuClose} variant="contained">OK</Button>
        </Box>
      </Dialog>

      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
        <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
      </Menu>

      <Dialog
        open={openResponseDialog}
        onClose={handleCloseResponseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#013E7D', fontWeight: 600, color: '#fff', pb: 2 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
            Survey Response Details
          </Typography>
          {selectedSurveyResponse.responseDetails && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
              {selectedSurveyResponse.responseDetails.surveyType === 'project' && selectedSurveyResponse.responseDetails.projectName && (
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  <strong>Project:</strong> {selectedSurveyResponse.responseDetails.projectName}
                </Typography>
              )}
              {selectedSurveyResponse.responseDetails.clientName && (
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  <strong>Client:</strong> {selectedSurveyResponse.responseDetails.clientName}
                </Typography>
              )}
              {selectedSurveyResponse.responseDetails.customerName && (
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  <strong>Customer:</strong> {selectedSurveyResponse.responseDetails.customerName}
                  {selectedSurveyResponse.responseDetails.customerEmail && ` (${selectedSurveyResponse.responseDetails.customerEmail})`}
                </Typography>
              )}
            </Box>
          )}
          <IconButton
            onClick={handleCloseResponseDialog}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {selectedSurveyResponse.surveyId && selectedSurveyResponse.clientId && (
            <ResponseTemplate
              surveyId={selectedSurveyResponse.surveyId}
              clientId={selectedSurveyResponse.clientId}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SurveyDashboard;


